import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  listenToShortstayData,
  upsertShortstay,
  upsertShortDiscount,
} from "../../../../redux/shortstaysSlice";
import { Minus, Plus, Trash2, Tag, Clock, Home, Users, Timer, BedDouble, CalendarDays, CalendarRange, Repeat } from "lucide-react";

/* ── tokens ── */
const T = {
  bg: "#F1EFE8", surface: "#ffffff", border: "#D3D1C7",
  border2: "#B4B2A9", text: "#2C2C2A", textSoft: "#5F5E5A",
  muted: "#888780", accent: "#2C2C2A", accentFg: "#F1EFE8",
  gold: "#BA7517", goldLight: "#FAEEDA", goldBorder: "#FAC775",
  green: "#3B6D11", greenLight: "#EAF3DE",
  sans: "'Montserrat', sans-serif",
  serif: "'Cormorant Garamond', serif",
};

const FONT_CSS = `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Montserrat:wght@300;400;500;600&display=swap');`;

type RateType =
  | "perNight"
  | "perNightPerPerson"
  | "perHour"
  | "perHourPerPerson"
  | "perWeek"
  | "perMonth"
  | "perYear";

type ExtraItem = { name: string; price: number };
type DurationDiscount = {
  weeklyStayDiscount: number;
  monthlyStayDiscount: number;
  yearlyStayDiscount: number;
};

const RATE_TYPES: { key: RateType; label: string; sub: string; icon: React.ReactNode }[] = [
  { key: "perNight",          label: "Per Night",         sub: "Fixed nightly rate",        icon: <Home size={16} /> },
  { key: "perNightPerPerson", label: "Night / Person",    sub: "Multiplied by guest count",  icon: <Users size={16} /> },
  { key: "perHour",           label: "Per Hour",          sub: "Fixed hourly rate",          icon: <Timer size={16} /> },
  { key: "perHourPerPerson",  label: "Hour / Person",     sub: "Hourly × guest count",       icon: <BedDouble size={16} /> },
  { key: "perWeek",           label: "Per Week",          sub: "Billed per 7-night week",    icon: <CalendarDays size={16} /> },
  { key: "perMonth",          label: "Per Month",         sub: "Billed per 30-night month",  icon: <CalendarRange size={16} /> },
  { key: "perYear",           label: "Per Year",          sub: "Billed per 365-night year",  icon: <Repeat size={16} /> },
];

const RATE_UNIT_LABEL: Record<RateType, string> = {
  perNight:          "/ night",
  perNightPerPerson: "/ night / person",
  perHour:           "/ hour",
  perHourPerPerson:  "/ hour / person",
  perWeek:           "/ week",
  perMonth:          "/ month",
  perYear:           "/ year",
};

const INSTANT_DISCOUNTS = [
  { value: "none", label: "No discount" },
  { value: "is5",  label: "5%" },
  { value: "is10", label: "10%" },
  { value: "is25", label: "25%" },
  { value: "is50", label: "50%" },
  { value: "is80", label: "80%" },
];

const ShortPrice: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const currentListing = useSelector((s: RootState) => s.shortstays.currentListing);

  const [rateType,          setRateType]          = useState<RateType>("perNight");
  const [rate,              setRate]              = useState(5);
  const [extras,            setExtras]            = useState<ExtraItem[]>([]);
  const [newExtraName,      setNewExtraName]      = useState("");
  const [newExtraPrice,     setNewExtraPrice]     = useState(0);
  const [hasChanges,        setHasChanges]        = useState(false);
  const [selectedDiscount,  setSelectedDiscount]  = useState("none");
  const [saving,            setSaving]            = useState(false);
  const [saved,             setSaved]             = useState(false);

  const [duration, setDuration] = useState<DurationDiscount>({
    weeklyStayDiscount: 0,
    monthlyStayDiscount: 0,
    yearlyStayDiscount: 0,
  });
  const [durationSaving, setDurationSaving] = useState(false);
  const [durationSaved,  setDurationSaved]  = useState(false);

  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
  }, [dispatch, listingId]);

  useEffect(() => {
    if (!currentListing) return;
    setRate(currentListing.rate || 5);
    if (currentListing.rateType) setRateType(currentListing.rateType as RateType);
    if (currentListing.extras) {
      setExtras(
        Object.entries(currentListing.extras).map(([name, price]) => ({
          name, price: Number(price),
        }))
      );
    }
    if (currentListing.discounts) {
      const found = Object.entries(currentListing.discounts).find(([, v]) => v === true);
      setSelectedDiscount(found ? found[0] : "none");
    }
    if (currentListing.bookingDurationDiscount) {
      setDuration({
        weeklyStayDiscount:  currentListing.bookingDurationDiscount.weeklyStayDiscount  || 0,
        monthlyStayDiscount: currentListing.bookingDurationDiscount.monthlyStayDiscount || 0,
        yearlyStayDiscount:  currentListing.bookingDurationDiscount.yearlyStayDiscount  || 0,
      });
    }
  }, [currentListing]);

  const extraCharges = extras.reduce((s, e) => s + (e.price || 0), 0);

  const adjustRate = (step: number) => {
    setRate(p => { setHasChanges(true); return Math.max(5, Math.min(10000, p + step)); });
  };

  const handleRateTypeChange = (key: RateType) => {
    setRateType(key);
    setHasChanges(true);
  };

  const addExtra = () => {
    if (!newExtraName.trim() || newExtraPrice <= 0) return;
    if (extras.some(e => e.name.toLowerCase() === newExtraName.toLowerCase())) return;
    setExtras(p => [...p, { name: newExtraName.trim(), price: newExtraPrice }]);
    setNewExtraName(""); setNewExtraPrice(0); setHasChanges(true);
  };

  const updateExtra = (i: number, field: "name" | "price", val: string | number) => {
    setExtras(p => p.map((item, idx) =>
      idx === i ? { ...item, [field]: field === "price" ? Number(val) : String(val) } : item
    ));
    setHasChanges(true);
  };

  const removeExtra = (i: number) => {
    setExtras(p => p.filter((_, idx) => idx !== i));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!listingId || !hasChanges) return;
    setSaving(true);
    const extrasObj: Record<string, number> = {};
    extras.forEach(e => { extrasObj[e.name] = e.price; });
    await dispatch(upsertShortstay({
      listingId,
      data: { rate, rateType, extraCharges, extras: extrasObj },
    }));
    setSaving(false); setSaved(true); setHasChanges(false);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleDiscountChange = async (value: string) => {
    setSelectedDiscount(value);
    if (!listingId) return;
    if (value === "none") {
      await dispatch(upsertShortDiscount({ listingId, type: "is5", active: false }));
    } else {
      await dispatch(upsertShortDiscount({ listingId, type: value as any, active: true }));
    }
  };

  const handleDurationSave = async () => {
    if (!listingId) return;
    setDurationSaving(true);
    await dispatch(upsertShortstay({ listingId, data: { bookingDurationDiscount: duration } }));
    setDurationSaving(false); setDurationSaved(true);
    setTimeout(() => setDurationSaved(false), 2000);
  };

  const setDur = (k: keyof DurationDiscount, v: number) =>
    setDuration(p => ({ ...p, [k]: Math.max(0, Math.min(100, v)) }));

  return (
    <div style={s.page}>
      <style>{FONT_CSS}</style>

      {/* ── heading row ── */}
      <div style={s.headRow}>
        <div>
          <p style={s.stepLabel}>Listing settings</p>
          <p style={s.title}>Pricing</p>
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{ ...s.saveBtn, ...(hasChanges ? s.saveBtnOn : s.saveBtnOff) }}
        >
          {saving ? "Saving…" : saved ? "Saved ✓" : "Save pricing"}
        </button>
      </div>

      {/* ── RATE TYPE ── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <p style={s.cardTitle}>How you charge</p>
          <p style={s.cardSub}>Select your billing model</p>
        </div>

        <div style={s.rateTypeGrid}>
          {RATE_TYPES.map(({ key, label, sub, icon }) => {
            const active = rateType === key;
            return (
              <button
                key={key}
                onClick={() => handleRateTypeChange(key)}
                style={{
                  ...s.rateTypeCard,
                  ...(active ? s.rateTypeCardActive : {}),
                }}
              >
                <span style={{
                  ...s.rateTypeIcon,
                  color: active ? T.gold : T.muted,
                  background: active ? T.goldLight : T.bg,
                  border: `1px solid ${active ? T.goldBorder : T.border}`,
                }}>
                  {icon}
                </span>
                <div style={{ textAlign: "left" }}>
                  <p style={{
                    fontSize: 12, fontWeight: 500,
                    color: active ? T.text : T.textSoft,
                    marginBottom: 2,
                  }}>
                    {label}
                  </p>
                  <p style={{ fontSize: 10, color: T.muted }}>{sub}</p>
                </div>
                {active && (
                  <span style={s.rateTypeCheck}>✓</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── NIGHTLY / HOURLY RATE ── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <p style={s.cardTitle}>Base rate</p>
        </div>

        <div style={s.rateHero}>
          <span style={s.rateBig}>€{rate.toLocaleString()}</span>
          <span style={s.rateUnit}>{RATE_UNIT_LABEL[rateType]}</span>
        </div>

        <div style={s.rateRow}>
          <button style={s.circBtn} onClick={() => adjustRate(-10)}>
            <Minus size={14} color={T.text} />
          </button>
          <input
            type="number"
            value={rate}
            onChange={e => {
              const v = Math.max(5, Math.min(10000, Number(e.target.value)));
              setRate(v); setHasChanges(true);
            }}
            style={s.rateInput}
          />
          <button style={s.circBtn} onClick={() => adjustRate(10)}>
            <Plus size={14} color={T.text} />
          </button>
        </div>
      </div>

      {/* ── EXTRA CHARGES ── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <p style={s.cardTitle}>Extra charges</p>
          {extraCharges > 0 && (
            <span style={s.totalBadge}>Total €{extraCharges.toLocaleString()}</span>
          )}
        </div>

        <div style={s.addRow}>
          <input
            style={s.input}
            placeholder="Extra name (e.g. Cleaning fee)"
            value={newExtraName}
            onChange={e => setNewExtraName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addExtra()}
          />
          <div style={s.priceGroup}>
            <button style={s.priceBtn} onClick={() => setNewExtraPrice(p => Math.max(0, p - 5))}>−</button>
            <input
              type="number"
              value={newExtraPrice}
              onChange={e => setNewExtraPrice(Number(e.target.value))}
              style={s.priceIn}
            />
            <button style={s.priceBtn} onClick={() => setNewExtraPrice(p => Math.min(p + 5, 5000))}>+</button>
          </div>
          <button style={s.addBtn} onClick={addExtra}>
            <Plus size={14} color={T.accentFg} />
          </button>
        </div>

        {extras.length === 0 && (
          <p style={{ fontSize: 12, color: T.muted, textAlign: "center", padding: "12px 0" }}>
            No extras added yet
          </p>
        )}
        {extras.map((item, i) => (
          <div key={i} style={s.extraRow}>
            <input
              value={item.name}
              onChange={e => updateExtra(i, "name", e.target.value)}
              style={{ ...s.input, flex: 1 }}
            />
            <div style={s.priceGroup}>
              <span style={{ fontSize: 12, color: T.muted, padding: "0 6px" }}>€</span>
              <input
                type="number"
                value={item.price}
                onChange={e => updateExtra(i, "price", e.target.value)}
                style={{ ...s.priceIn, width: 60 }}
              />
            </div>
            <button style={s.removeBtn} onClick={() => removeExtra(i)}>
              <Trash2 size={13} color={T.muted} />
            </button>
          </div>
        ))}
      </div>

      {/* ── INSTANT DISCOUNT ── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ ...s.iconPill, background: T.goldLight }}>
              <Tag size={13} color={T.gold} />
            </div>
            <div>
              <p style={s.cardTitle}>Instant discount</p>
              <p style={s.cardSub}>Applies to any booking regardless of duration</p>
            </div>
          </div>
        </div>

        <div style={s.discGrid}>
          {INSTANT_DISCOUNTS.map(({ value, label }) => {
            const active = selectedDiscount === value;
            return (
              <button
                key={value}
                onClick={() => handleDiscountChange(value)}
                style={{ ...s.discPill, ...(active ? s.discPillActive : {}) }}
              >
                {active && <span style={s.discCheck}>✓ </span>}
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── DURATION DISCOUNT ── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ ...s.iconPill, background: T.greenLight }}>
              <Clock size={13} color={T.green} />
            </div>
            <div>
              <p style={s.cardTitle}>Duration discount</p>
              <p style={s.cardSub}>Applies based on how long the guest stays</p>
            </div>
          </div>
          <button
            onClick={handleDurationSave}
            disabled={durationSaving}
            style={{ ...s.saveBtn, ...s.saveBtnOn, fontSize: 11, padding: "7px 16px" }}
          >
            {durationSaving ? "Saving…" : durationSaved ? "Saved ✓" : "Save"}
          </button>
        </div>

        <div style={s.durGrid}>
          {([
            { key: "weeklyStayDiscount",  label: "Weekly stay",  sub: "7+ nights",   color: T.goldLight,  icon: "🗓" },
            { key: "monthlyStayDiscount", label: "Monthly stay", sub: "28+ nights",  color: T.greenLight, icon: "📅" },
            { key: "yearlyStayDiscount",  label: "Yearly stay",  sub: "365+ nights", color: "#E6F1FB",    icon: "🏠" },
          ] as const).map(({ key, label, sub, color, icon }) => (
            <div key={key} style={{ ...s.durCard, background: color }}>
              <span style={{ fontSize: 20 }}>{icon}</span>
              <p style={{ fontSize: 13, fontWeight: 500, color: T.text, marginTop: 6 }}>{label}</p>
              <p style={{ fontSize: 10, color: T.muted, marginBottom: 12 }}>{sub}</p>
              <div style={s.stepRow}>
                <button style={s.stepBtn} onClick={() => setDur(key, duration[key] - 5)}>
                  <Minus size={11} color={T.text} />
                </button>
                <div style={s.stepDisplay}>
                  <span style={s.stepNum}>{duration[key]}</span>
                  <span style={s.stepPct}>%</span>
                </div>
                <button style={s.stepBtn} onClick={() => setDur(key, duration[key] + 5)}>
                  <Plus size={11} color={T.text} />
                </button>
              </div>
              <input
                type="range"
                min={0} max={80} step={5}
                value={duration[key]}
                onChange={e => setDur(key, Number(e.target.value))}
                style={{ width: "100%", marginTop: 10, accentColor: T.accent }}
              />
              {duration[key] > 0 && (
                <p style={{ fontSize: 10, color: T.green, marginTop: 6, fontWeight: 500 }}>
                  Guest saves €{Math.round(rate * duration[key] / 100)} / night
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      <p style={{ fontSize: 11, color: T.muted, textAlign: "center", marginTop: 8, paddingBottom: 20 }}>
        Tip: Add duration discounts to attract long-term guests and reduce vacancy.
      </p>
    </div>
  );
};

export default ShortPrice;

/* ── styles ── */
const s: Record<string, React.CSSProperties> = {
  headRow:   { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24 },
  stepLabel: { fontSize: 10, fontWeight: 500, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 },
  title:     { fontSize: 24, fontWeight: 500, color: T.text, fontFamily: "'Cormorant Garamond', serif" },

  saveBtn:    { borderRadius: 20, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 500, fontFamily: T.sans, padding: "9px 20px", transition: "all .2s" },
  saveBtnOn:  { background: T.accent, color: T.accentFg },
  saveBtnOff: { background: T.border, color: T.muted, cursor: "not-allowed" },

  card:      { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "20px 20px 24px", marginBottom: 16 },
  cardHead:  { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 },
  cardTitle: { fontSize: 14, fontWeight: 500, color: T.text },
  cardSub:   { fontSize: 11, color: T.muted, marginTop: 1 },
  iconPill:  { width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  totalBadge: { fontSize: 11, fontWeight: 500, background: T.goldLight, color: T.gold, border: `0.5px solid ${T.goldBorder}`, borderRadius: 20, padding: "3px 10px" },

  /* rate type grid */
  rateTypeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 10,
  },
  rateTypeCard: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 14px", borderRadius: 10,
    border: `1.5px solid ${T.border}`,
    background: T.bg, cursor: "pointer",
    transition: "all .18s", fontFamily: T.sans,
    position: "relative" as const,
  },
  rateTypeCardActive: {
    borderColor: T.goldBorder,
    background: T.goldLight,
  },
  rateTypeIcon: {
    width: 34, height: 34, borderRadius: 8,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, transition: "all .18s",
  },
  rateTypeCheck: {
    position: "absolute" as const, top: 8, right: 10,
    fontSize: 10, fontWeight: 600, color: T.gold,
  },

  /* rate */
  rateHero:  { textAlign: "center" as const, marginBottom: 16 },
  rateBig:   { fontSize: 56, fontWeight: 300, color: T.text, fontFamily: "'Cormorant Garamond', serif", letterSpacing: "-0.02em" },
  rateUnit:  { fontSize: 13, color: T.muted, marginLeft: 6 },
  rateRow:   { display: "flex", alignItems: "center", justifyContent: "center", gap: 10 },
  circBtn:   { width: 36, height: 36, borderRadius: "50%", border: `1.5px solid ${T.border}`, background: T.surface, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
  rateInput: { width: 110, fontSize: 20, fontWeight: 500, textAlign: "center" as const, padding: "8px 10px", borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontFamily: T.sans, outline: "none" },

  /* extras */
  addRow:    { display: "flex", alignItems: "center", gap: 8, marginBottom: 12 },
  extraRow:  { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  input:     { flex: 1, background: T.bg, border: `1px solid ${T.border}`, padding: "9px 12px", borderRadius: 8, fontSize: 13, color: T.text, outline: "none", fontFamily: T.sans },
  priceGroup: { display: "flex", alignItems: "center", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" },
  priceBtn:  { background: "transparent", border: "none", padding: "8px 10px", fontSize: 16, cursor: "pointer", color: T.text, fontWeight: 500 },
  priceIn:   { width: 64, border: "none", textAlign: "center" as const, fontSize: 13, fontWeight: 500, outline: "none", background: "transparent", color: T.text, fontFamily: T.sans },
  addBtn:    { width: 36, height: 36, borderRadius: 8, background: T.accent, border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
  removeBtn: { width: 30, height: 30, borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },

  /* instant discount */
  discGrid:       { display: "flex", flexWrap: "wrap" as const, gap: 8 },
  discPill:       { padding: "8px 16px", borderRadius: 20, border: `1.5px solid ${T.border}`, background: T.bg, fontSize: 12, fontWeight: 500, color: T.textSoft, cursor: "pointer", transition: "all .15s", fontFamily: T.sans },
  discPillActive: { background: T.accent, borderColor: T.accent, color: T.accentFg },
  discCheck:      { fontSize: 10 },

  /* duration */
  durGrid:     { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 },
  durCard:     { borderRadius: 12, padding: "16px 14px", border: `1px solid ${T.border}`, textAlign: "center" as const },
  stepRow:     { display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  stepBtn:     { width: 28, height: 28, borderRadius: "50%", background: T.surface, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
  stepDisplay: { display: "flex", alignItems: "baseline", gap: 2 },
  stepNum:     { fontSize: 22, fontWeight: 500, color: T.text, fontFamily: "'Cormorant Garamond', serif" },
  stepPct:     { fontSize: 12, color: T.muted },
};